import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputNumber } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { Tooltip } from 'primeng/tooltip';
import { AuthService } from '@brightside/core/auth';
import type {
  FinancialSnapshotDocument,
  FinancialSnapshotResponse,
  FinancialSnapshotTemplate,
  SnapshotCategoryData,
} from '../financial-snapshot.model';
import {
  countEnteredUiRows,
  FINANCIAL_SNAPSHOT_UI_ROW_COUNT,
  recomputeSnapshot,
} from '../compute/financial-snapshot-compute';
import { FinancialSnapshotStore } from '../financial-snapshot.store';
import { FinancialSnapshotAnalytics } from '../analytics/financial-snapshot-analytics';
import {
  AMOUNT_VALIDATION_MESSAGE,
  buildCategoriesFromForm,
  buildDocumentFromForm,
  categorySubKey,
  categorySumFromForm,
  categoryTotalKey,
  changedCategoryKeys,
  clearedCategoryKeys,
  formValuesEqual,
  hasItemizedValues,
  hasNegativeAmounts,
  initFormValues,
  initOtherCustoms,
  incomeCategoryKey,
  isNonNegativeAmount,
  newOtherCustomKey,
  OTHER_CUSTOM_LABEL_MAX_LEN,
  otherCustomsEqual,
  otherSubKey,
  sanitizeOtherCustomLabel,
  SAVINGS_KEY,
  spendingCategoryKeys,
  stampFaAudit,
  type OtherCustomEntry,
  type SnapshotFormValues,
} from './financial-snapshot-edit.model';

export interface FinancialSnapshotEditDialogData {
  guid: string;
  snapshot: FinancialSnapshotResponse | null;
  template: FinancialSnapshotTemplate;
  store: FinancialSnapshotStore;
}

@Component({
  selector: 'bn-financial-snapshot-edit-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    CurrencyPipe,
    Button,
    InputNumber,
    InputTextModule,
    ConfirmDialog,
    Tooltip,
  ],
  providers: [ConfirmationService],
  templateUrl: './financial-snapshot-edit-dialog.component.html',
  styleUrl: './financial-snapshot-edit-dialog.component.scss',
})
export class FinancialSnapshotEditDialogComponent {
  private readonly _ref = inject(DynamicDialogRef);
  private readonly _config = inject(DynamicDialogConfig);
  private readonly _confirmation = inject(ConfirmationService);
  private readonly _messages = inject(MessageService);
  private readonly _auth = inject(AuthService);
  private readonly _analytics = inject(FinancialSnapshotAnalytics);

  private readonly _data = this._config.data as FinancialSnapshotEditDialogData;

  readonly guid = this._data.guid;
  readonly template = this._data.template;
  readonly store = this._data.store;

  readonly formValues = signal<SnapshotFormValues>(
    initFormValues(this._data.snapshot, this.template)
  );
  /** Diff baseline — rebased from server after a partial PATCH failure. */
  private readonly _baselineSnapshot = signal<FinancialSnapshotResponse | null>(this._data.snapshot);
  private readonly _initialValues = signal<SnapshotFormValues>(
    initFormValues(this._data.snapshot, this.template)
  );
  readonly otherCustoms = signal<OtherCustomEntry[]>(initOtherCustoms(this._data.snapshot));
  private readonly _initialCustoms = signal<OtherCustomEntry[]>(
    initOtherCustoms(this._data.snapshot)
  );

  readonly expanded = signal<Record<string, boolean>>({
    [incomeCategoryKey(this.template) ?? 'monthlyhouseholdincome']: true,
  });

  readonly saving = signal(false);
  readonly saveError = signal<string | null>(null);
  /** Shown when a typed amount is cleared for being negative (InputNumber min=0 may still emit). */
  readonly amountValidationHint = signal<string | null>(null);
  /** Fields whose last entry was rejected as negative, so they render invalid. */
  private readonly _negativeFieldKeys = signal<ReadonlySet<string>>(new Set<string>());
  /** Set once a save is blocked for emptying a saved category; the message then tracks edits live. */
  private readonly _clearedGuardTripped = signal(false);

  readonly isSubmitted = !!this._data.snapshot?.submittedDate;

  /**
   * True when the session no longer points at this dialog's client — switched to another
   * client or cleared entirely. Either way the dialog is stale, so writes are blocked.
   */
  readonly sessionMismatch = computed(() => this.store.sessionGuid() !== this.guid);

  readonly incomeKey = incomeCategoryKey(this.template) ?? 'monthlyhouseholdincome';
  readonly spendingKeys = spendingCategoryKeys(this.template);
  readonly otherKey = 'other';
  readonly otherNamed = [
    {
      key: 'activities',
      label: this.template['other']?.subCategories?.['activities']?.label ?? 'Activities',
      shortLabel: 'Activities',
    },
    {
      key: 'medical',
      label: this.template['other']?.subCategories?.['medical']?.label ?? 'Medical',
      shortLabel: 'Medical',
    },
    {
      key: 'personal',
      label: this.template['other']?.subCategories?.['personal']?.label ?? 'Personal',
      shortLabel: 'Personal',
    },
  ] as const;
  readonly savingsFormKey = SAVINGS_KEY;
  readonly otherCustomLabelMaxLen = OTHER_CUSTOM_LABEL_MAX_LEN;

  readonly preview = computed(() => {
    const doc = buildDocumentFromForm(
      this.guid,
      this.formValues(),
      this.template,
      this._baselineSnapshot(),
      this.otherCustoms()
    );
    return recomputeSnapshot(doc, this.template);
  });

  readonly enteredCount = computed(() =>
    countEnteredUiRows(
      buildDocumentFromForm(
        this.guid,
        this.formValues(),
        this.template,
        this._baselineSnapshot(),
        this.otherCustoms()
      ),
      this.template
    )
  );

  readonly canSubmit = computed(
    () => !this.isSubmitted && this.enteredCount() >= FINANCIAL_SNAPSHOT_UI_ROW_COUNT
  );

  readonly dirty = computed(
    () =>
      !formValuesEqual(this.formValues(), this._initialValues()) ||
      !otherCustomsEqual(this.otherCustoms(), this._initialCustoms())
  );

  /**
   * Saved categories the FA has emptied. Recomputed while typing so the banner and the
   * red fields clear as soon as an amount (including 0) is restored.
   */
  readonly clearedCategories = computed<string[]>(() => {
    if (!this._clearedGuardTripped() || !this.isSubmitted) {
      return [];
    }
    const previous = this._baselineSnapshot()?.categories ?? {};
    try {
      const next = buildCategoriesFromForm(this.formValues(), this.template, this.otherCustoms());
      return clearedCategoryKeys(previous, next);
    } catch {
      // Negative amounts are reported by their own hint.
      return [];
    }
  });

  readonly clearedCategoriesMessage = computed<string | null>(() => {
    const keys = this.clearedCategories();
    if (keys.length === 0) {
      return null;
    }
    const names = keys.map((key) => this.displayCategoryName(key)).join(', ');
    return `${names} can't be left blank once saved. Enter 0 where the client has no amount, or restore the previous amounts.`;
  });

  readonly totalUiRows = FINANCIAL_SNAPSHOT_UI_ROW_COUNT;

  private _sessionClosePromptOpen = false;

  constructor() {
    // Prefer dialog-owned close so dirty edits go through a confirm (card force-close skipped that).
    // Stay is not offered: the active client already changed, so the dialog must close.
    effect(() => {
      if (!this.sessionMismatch()) {
        return;
      }
      untracked(() => this._closeForSessionChange());
    });
  }

  incomeSubs(): { key: string; label: string; formKey: string }[] {
    const tmpl = this.template[this.incomeKey];
    if (!tmpl?.subCategories) {
      return [];
    }
    return Object.entries(tmpl.subCategories)
      .sort((a, b) => a[1].order - b[1].order)
      .map(([key, sub]) => ({
        key,
        label: sub.label,
        formKey: categorySubKey(this.incomeKey, key),
      }));
  }

  spendingSubs(catKey: string): { key: string; label: string; formKey: string }[] {
    const tmpl = this.template[catKey];
    if (!tmpl?.subCategories) {
      return [];
    }
    return Object.entries(tmpl.subCategories)
      .sort((a, b) => a[1].order - b[1].order)
      .map(([key, sub]) => ({
        key,
        label: sub.label,
        formKey: categorySubKey(catKey, key),
      }));
  }

  categoryName(catKey: string): string {
    return this.template[catKey]?.name ?? catKey;
  }

  displayCategoryName(catKey: string): string {
    const name = this.categoryName(catKey);
    return name === 'Debt Repayment' ? 'Debt payments' : name;
  }

  /** Template `instructions` for a top-level category (tooltip copy). */
  categoryInstructions(catKey: string): string | null {
    const text = this.template[catKey]?.instructions?.trim();
    return text || null;
  }

  categorySum(catKey: string): number | null {
    return categorySumFromForm(catKey, this.formValues(), this.template, this.otherCustoms());
  }

  categoryTotalFormKey(catKey: string): string {
    return categoryTotalKey(catKey);
  }

  /**
   * True when the total comes from itemized amounts. When false the category total was
   * entered directly (client or FA) and stays editable.
   */
  isItemized(catKey: string): boolean {
    return hasItemizedValues(catKey, this.formValues(), this.template, this.otherCustoms());
  }

  percentOfIncome(amount: number | null): number | null {
    const income = this.preview().metrics.householdIncome;
    if (amount === null || income === null || income <= 0) {
      return null;
    }
    return Math.round((amount / income) * 100);
  }

  otherNamedFormKey(sub: string): string {
    return otherSubKey(sub);
  }

  /** Field key for custom Other amounts (kept under `other` so category errors cover them). */
  otherCustomFormKey(key: string): string {
    return `other.custom.${key}`;
  }

  /** True when the field is part of a cleared category or holds a rejected amount. */
  hasFieldError(formKey: string): boolean {
    if (this._negativeFieldKeys().has(formKey)) {
      return true;
    }
    return this.clearedCategories().includes(formKey.split('.')[0]);
  }

  categoryHasError(catKey: string): boolean {
    return this.clearedCategories().includes(catKey);
  }

  getNum(formKey: string): number | null {
    const v = this.formValues()[formKey];
    return typeof v === 'number' ? v : null;
  }

  setNum(formKey: string, value: number | null): void {
    this._noteNegativeCleared(formKey, value);
    this.formValues.update((m) => ({
      ...m,
      [formKey]: isNonNegativeAmount(value) ? value : undefined,
    }));
  }

  addOtherCustom(): void {
    this.otherCustoms.update((list) => [
      ...list,
      { key: newOtherCustomKey(), label: '', value: undefined },
    ]);
    this.expanded.update((m) => ({ ...m, [this.otherKey]: true }));
  }

  removeOtherCustom(key: string): void {
    this._noteNegativeCleared(this.otherCustomFormKey(key), null);
    this.otherCustoms.update((list) => list.filter((c) => c.key !== key));
  }

  setOtherCustomLabel(key: string, label: string): void {
    this.otherCustoms.update((list) =>
      list.map((c) => (c.key === key ? { ...c, label: label.slice(0, OTHER_CUSTOM_LABEL_MAX_LEN) } : c))
    );
  }

  setOtherCustomValue(key: string, value: number | null): void {
    this._noteNegativeCleared(this.otherCustomFormKey(key), value);
    this.otherCustoms.update((list) =>
      list.map((c) =>
        c.key === key
          ? {
              ...c,
              value: isNonNegativeAmount(value) ? value : undefined,
            }
          : c
      )
    );
  }

  getOtherCustomValue(key: string): number | null {
    const entry = this.otherCustoms().find((c) => c.key === key);
    return typeof entry?.value === 'number' ? entry.value : null;
  }

  /** Blur: trim / cap label for persistence readiness. */
  commitOtherCustomLabel(key: string): void {
    this.otherCustoms.update((list) =>
      list.map((c) => (c.key === key ? { ...c, label: sanitizeOtherCustomLabel(c.label) } : c))
    );
  }

  toggleExpanded(catKey: string): void {
    this.expanded.update((m) => ({ ...m, [catKey]: !m[catKey] }));
  }

  isExpanded(catKey: string): boolean {
    return !!this.expanded()[catKey];
  }

  tryClose(): void {
    if (!this.dirty()) {
      this._ref.close({ cancelled: true });
      return;
    }
    this._confirmation.confirm({
      message: 'You have unsaved changes — are you sure you want to discard them?',
      header: 'Unsaved changes',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Discard',
      rejectLabel: 'Stay',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this._ref.close({ cancelled: true }),
    });
  }

  /**
   * Active client changed (or was cleared) under an open edit dialog. Never leave a stale-guid
   * dialog open. Dirty → confirm (Discard only); clean → close immediately. Toast explains it.
   *
   * Discard is the only exit: dismissing via X / Escape / mask would strand the prompt flag and
   * leave the stale dialog open with no way to re-prompt.
   */
  private _closeForSessionChange(): void {
    if (this._sessionClosePromptOpen) {
      return;
    }
    if (!this.dirty()) {
      this._ref.close({ cancelled: true, sessionChanged: true });
      return;
    }
    this._sessionClosePromptOpen = true;
    this._confirmation.confirm({
      message:
        'The active client changed. Unsaved financial snapshot edits for the previous client will be discarded.',
      header: 'Unsaved changes',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Discard',
      rejectVisible: false,
      closable: false,
      closeOnEscape: false,
      dismissableMask: false,
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this._sessionClosePromptOpen = false;
        this._messages.add({
          severity: 'warn',
          summary: 'Edits discarded',
          detail: 'Unsaved financial snapshot edits were discarded because the active client changed.',
        });
        this._ref.close({ cancelled: true, sessionChanged: true });
      },
      // Belt and suspenders: any dismissal path must leave the prompt re-openable.
      reject: () => {
        this._sessionClosePromptOpen = false;
      },
    });
  }

  private _noteNegativeCleared(formKey: string, value: number | null): void {
    const negative = typeof value === 'number' && !Number.isNaN(value) && value < 0;
    this._negativeFieldKeys.update((keys) => {
      if (negative === keys.has(formKey)) {
        return keys;
      }
      const next = new Set(keys);
      if (negative) {
        next.add(formKey);
      } else {
        next.delete(formKey);
      }
      return next;
    });
    this.amountValidationHint.set(
      this._negativeFieldKeys().size > 0 ? AMOUNT_VALIDATION_MESSAGE : null
    );
  }

  async saveProgress(): Promise<void> {
    await this._persist({ submit: false });
  }

  async submitSnapshot(): Promise<void> {
    if (!this.canSubmit()) {
      return;
    }
    await this._persist({ submit: true });
  }

  private async _persist(opts: { submit: boolean }): Promise<void> {
    if (this.sessionMismatch()) {
      this._closeForSessionChange();
      return;
    }
    this.saving.set(true);
    this.saveError.set(null);
    const succeededKeys: string[] = [];
    try {
      // A rejected amount is sanitized out of the form, so the form values alone look valid —
      // saving here would silently drop or blank the field the FA is still being asked to fix.
      // Amount validation owns its own banner, so it is never mirrored into saveError.
      if (
        this._negativeFieldKeys().size > 0 ||
        hasNegativeAmounts(this.formValues(), this.otherCustoms())
      ) {
        this.amountValidationHint.set(AMOUNT_VALIDATION_MESSAGE);
        return;
      }
      const user = await this._auth.getUser();
      const editorId = user?.sub ?? 'fa-unknown';
      const categories = stampFaAudit(
        buildCategoriesFromForm(this.formValues(), this.template, this.otherCustoms()),
        editorId
      );
      const fieldsEntered = countEnteredUiRows(
        { guid: this.guid, categories } satisfies FinancialSnapshotDocument,
        this.template
      );

      if (this.isSubmitted) {
        const previous = this._baselineSnapshot()?.categories ?? {};
        const cleared = clearedCategoryKeys(previous, categories);
        // Deliberately all-or-nothing: an emptied category blocks the whole save rather than
        // letting other categories through, so the FA never has to reason about which of their
        // edits landed. Resolving the flagged category is one entry away, then Save works.
        if (cleared.length > 0) {
          this._clearedGuardTripped.set(true);
          // Open the offending categories so the red fields are visible.
          this.expanded.update((m) => ({
            ...m,
            ...Object.fromEntries(cleared.map((key) => [key, true])),
          }));
          return;
        }
        const changed = changedCategoryKeys(previous, categories).filter((key) => categories[key]);
        if (changed.length === 0) {
          this._messages.add({
            severity: 'info',
            summary: 'No changes',
            detail: 'Nothing to save.',
          });
          this._ref.close({ cancelled: false, saved: true });
          return;
        }
        for (const key of changed) {
          const body: Record<string, SnapshotCategoryData> = { [key]: categories[key] };
          await this.store.savePatch(this.guid, body);
          succeededKeys.push(key);
        }
      } else {
        await this.store.savePost({
          guid: this.guid,
          categories,
          createdDate: this._baselineSnapshot()?.createdDate ?? this._data.snapshot?.createdDate,
          submittedDate: opts.submit ? new Date().toISOString() : undefined,
          lastEditedBy: 'fa',
          lastEditedById: editorId,
        });
      }

      this._analytics.saved(this.guid, fieldsEntered);
      this._messages.add({
        severity: 'success',
        summary: opts.submit ? 'Snapshot submitted' : 'Progress saved',
        detail: opts.submit
          ? 'Financial snapshot submitted successfully.'
          : 'Financial snapshot progress saved.',
      });
      this._ref.close({ cancelled: false, saved: true, submitted: opts.submit });
    } catch (err) {
      if (this.isSubmitted) {
        await this._rebaseFromServerAfterPatchFailure(succeededKeys, err);
      } else {
        this.saveError.set(err instanceof Error ? err.message : 'Save failed');
      }
    } finally {
      this.saving.set(false);
    }
  }

  /**
   * After a mid-loop PATCH failure, reload server truth and rebase the diff baseline
   * so retry only sends remaining unsaved categories. Keeps current form edits.
   */
  private async _rebaseFromServerAfterPatchFailure(
    succeededKeys: string[],
    err: unknown
  ): Promise<void> {
    const failDetail = err instanceof Error ? err.message : 'Save failed';
    try {
      await this.store.reload();
    } catch {
      // Still surface the save failure even if reload fails.
    }

    const snap = this.store.snapshot();
    this._baselineSnapshot.set(snap);
    this._initialValues.set(initFormValues(snap, this.template));
    this._initialCustoms.set(initOtherCustoms(snap));

    const savedNames = succeededKeys.map((k) => this.displayCategoryName(k));
    if (savedNames.length === 0) {
      this.saveError.set(
        `${failDetail} Nothing was saved. We've refreshed from the server — review and try again.`
      );
      return;
    }
    this.saveError.set(
      `${failDetail} Saved: ${savedNames.join(', ')}. Remaining changes were not saved. We've refreshed from the server — review and try again.`
    );
  }
}
